import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const TZS = ["Australia/Sydney", "Australia/Melbourne", "Australia/Brisbane", "Australia/Adelaide", "Australia/Perth", "Australia/Hobart", "Australia/Darwin"];

const F = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="font-type text-[11px] uppercase tracking-wider text-[#8FA58A]">{label}</Label>
    {children}
  </div>
);

const RuleCard = ({ rule }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState({ ...rule });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value });
  const cls = "bg-[#121E2B] border-[#2C3E55] h-10 text-sm";
  const sel = "h-10 w-full rounded-md border border-[#2C3E55] bg-[#121E2B] px-3 text-sm";

  const save = async () => {
    setBusy(true);
    try {
      const { label, weekday, time, cadence, anchor_date, nth, timezone, link, notes, is_active } = form;
      const payload = { label, weekday: Number(weekday), time, cadence, link, notes, is_active };
      if (cadence === "fortnightly") payload.anchor_date = anchor_date;
      else payload.nth = Number(nth) || 1;
      if (rule.type === "national") payload.timezone = timezone;
      await api.put(`/admin/meetings/${rule.type}`, payload);
      toast.success(`${label} saved`);
      qc.invalidateQueries({ queryKey: ["admin-meetings"] });
      qc.invalidateQueries({ queryKey: ["meetings"] });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`admin-meeting-rule-${rule.type}`} className="rounded-2xl bg-[#1B2A41] border border-[#2C3E55] p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-type text-[11px] uppercase tracking-widest text-[#E2705A]">{rule.type}</p>
          <p className="text-xs text-[#F8FAFC]/60 mt-1">{rule.description}</p>
        </div>
        <label className="inline-flex items-center gap-2 text-xs">
          <input type="checkbox" data-testid="admin-meeting-active" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="accent-[#E2705A]" /> Active
        </label>
      </div>
      <F label="Label"><Input data-testid="admin-meeting-label" value={form.label} onChange={set("label")} className={cls} /></F>
      <div className="grid sm:grid-cols-3 gap-3">
        <F label="Day">
          <select data-testid="admin-meeting-weekday" value={form.weekday} onChange={set("weekday")} className={sel}>
            {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
          </select>
        </F>
        <F label="Time (24h)"><Input data-testid="admin-meeting-time" type="time" value={form.time} onChange={set("time")} className={cls} /></F>
        <F label="Cadence">
          <select data-testid="admin-meeting-cadence" value={form.cadence} onChange={set("cadence")} className={sel}>
            <option value="fortnightly">Fortnightly</option>
            <option value="monthly">Monthly</option>
          </select>
        </F>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {form.cadence === "fortnightly" ? (
          <F label="Cycle anchor date (a meeting date)"><Input data-testid="admin-meeting-anchor" type="date" value={form.anchor_date || ""} onChange={set("anchor_date")} className={cls} /></F>
        ) : (
          <F label="Which week of the month">
            <select data-testid="admin-meeting-nth" value={form.nth || 1} onChange={set("nth")} className={sel}>
              {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{["1st", "2nd", "3rd", "4th"][n - 1]}</option>)}
            </select>
          </F>
        )}
        {rule.type === "national" ? (
          <F label="Timezone">
            <select data-testid="admin-meeting-timezone" value={form.timezone || "Australia/Sydney"} onChange={set("timezone")} className={sel}>
              {TZS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </F>
        ) : (
          <F label="Timezone"><p className="h-10 flex items-center text-xs text-[#F8FAFC]/50">Each state's local time</p></F>
        )}
      </div>
      <F label="Video call link (optional)"><Input data-testid="admin-meeting-link" value={form.link || ""} onChange={set("link")} placeholder="https://meet.jit.si/roan-national" className={cls} /></F>
      <F label="Notes"><Input data-testid="admin-meeting-notes" value={form.notes || ""} onChange={set("notes")} className={cls} /></F>
      <button onClick={save} disabled={busy} data-testid="admin-meeting-save" className="h-10 px-5 rounded-full bg-[#E2705A] hover:bg-[#D35D46] disabled:opacity-60 font-type font-bold uppercase tracking-wider text-xs inline-flex items-center gap-2">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save
      </button>
    </div>
  );
};

export const MeetingsManager = () => {
  const { data: rules = [] } = useQuery({ queryKey: ["admin-meetings"], queryFn: () => api.get("/admin/meetings").then((r) => r.data) });
  const [running, setRunning] = useState(false);
  const runReminders = async () => {
    setRunning(true);
    try {
      await api.post("/admin/reminders/run");
      toast.success("Reminder check queued — see the Emails tab for anything sent");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setRunning(false);
    }
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[#F8FAFC]/70">Three recurring rules drive every date shown on the site, in calendars and in emails. Reminders go out automatically the day before each meeting (9am local).</p>
        <button onClick={runReminders} disabled={running} data-testid="admin-run-reminders" className="h-10 px-4 rounded-full border border-[#2C3E55] hover:border-[#8FA58A] text-xs font-semibold inline-flex items-center gap-2 disabled:opacity-60">
          {running && <Loader2 className="w-4 h-4 animate-spin" />} Run reminder check now
        </button>
      </div>
      <div className="grid lg:grid-cols-3 gap-4">{rules.map((r) => <RuleCard key={r.type + r.updated_at} rule={r} />)}</div>
    </div>
  );
};
